import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAvailableContexts, shouldShowContextSelector, getDefaultContext } from '../utils/permissions';
import { getContextConfig } from '../config/contexts';
import { Landmark, Sun, HeartHandshake } from 'lucide-react';
import { TopBar } from '../components/TopBar';

const iconMap: Record<string, React.ReactNode> = {
  'landmark': <Landmark className="w-12 h-12" />,
  'sun': <Sun className="w-12 h-12" />,
  'home': <HeartHandshake className="w-12 h-12" />,
};

export default function ContextSelectorPage() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const availableContexts = getAvailableContexts(usuario);
  const showSelector = shouldShowContextSelector(usuario);

  const getFirstName = () => {
    if (!usuario) return 'Usuario';
    return usuario.nombres?.trim().split(' ')[0] || 'Usuario';
  };

  useEffect(() => {
    if (!showSelector) {
      const defaultContext = getDefaultContext(usuario);
      if (defaultContext === 'lumisial') {
        navigate('/lumisial/home');
      } else if (defaultContext) {
        const config = getContextConfig(defaultContext);
        if (config) {
          navigate(config.path);
        }
      }
    }
  }, [showSelector, usuario, navigate]);

  if (!showSelector) {
    return null;
  }

  const handleContextSelect = (contextId: string) => {
    const config = getContextConfig(contextId);
    if (config) {
      if (contextId === 'lumisial') {
        navigate('/lumisial/home');
      } else {
        navigate(config.path);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <TopBar
        isMainHome={true}
        mainTitle="Gestión Administrativa Gnóstica"
      />

      <div className="flex items-center justify-center p-6 mt-12">
        <div className="max-w-5xl w-full">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              Bienvenido, {getFirstName()}
            </h2>
            <p className="text-lg text-gray-600">
              Selecciona el panel al que deseas acceder
            </p>
          </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {availableContexts.map((contextId) => {
            const config = getContextConfig(contextId);
            if (!config) return null;

            return (
              <button
                key={config.id}
                onClick={() => handleContextSelect(config.id)}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-center group hover:scale-105 border-2 border-transparent hover:border-blue-500"
              >
                <div className="flex justify-center mb-4 text-blue-600 group-hover:text-blue-700 transition-colors">
                  {iconMap[config.icon]}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {config.name}
                </h3>
                <p className="text-gray-600 text-sm">
                  {config.description}
                </p>
              </button>
            );
          })}
        </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Rol: <span className="font-semibold">{usuario?.rol?.nombre}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
